import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle2 } from "lucide-react";

const FIELD = "w-full h-11 rounded-xl bg-white/5 border border-white/10 px-4 text-sm placeholder:text-white/30 focus:outline-none focus:border-[#00E7FF]";

export default function Copyright() {
  const [sp] = useSearchParams();
  const pre = sp.get("video");
  const [f, setF] = useState({ video_url: pre ? `${window.location.origin}/watch/${pre}` : "", claimant_name: "", email: "",
    organization: "", original_work: "", description: "", signature: "", good_faith: false, accuracy: false });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF(x => ({ ...x, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/copyright/claims", f); setDone(data.id); }
    catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };

  if (done) return (
    <div className="mx-auto max-w-xl px-4 pt-16 text-center" data-testid="copyright-success">
      <CheckCircle2 size={48} className="mx-auto text-[#00E7FF]"/>
      <h1 className="h-font text-3xl font-extrabold mt-4">Claim received</h1>
      <p className="text-white/60 text-sm mt-2">Reference <span className="font-mono text-white/80">{done}</span>. Our team will review it and email you at {f.email}.</p>
      <Link to="/" className="text-[#00E7FF] text-sm mt-6 inline-block">← Back home</Link>
    </div>
  );

  return (
    <div className="mx-auto max-w-2xl px-4 sm:px-6 pt-8" data-testid="copyright-page">
      <h1 className="h-font text-3xl sm:text-4xl font-extrabold flex items-center gap-3"><ShieldCheck className="text-[#00E7FF]"/> Copyright claim</h1>
      <p className="text-sm text-white/60 mt-2">Report a Jixxeo video that infringes your copyright. Read our <Link to="/dmca" className="text-[#00E7FF]">DMCA Policy</Link> first.</p>
      <form onSubmit={submit} className="mt-6 space-y-4">
        <L label="Infringing video link *"><input required value={f.video_url} onChange={set("video_url")} placeholder="https://…/watch/…" className={FIELD} data-testid="cr-video-url"/></L>
        <div className="grid sm:grid-cols-2 gap-4">
          <L label="Full legal name *"><input required value={f.claimant_name} onChange={set("claimant_name")} className={FIELD} data-testid="cr-name"/></L>
          <L label="Email *"><input required type="email" value={f.email} onChange={set("email")} className={FIELD} data-testid="cr-email"/></L>
        </div>
        <L label="Company / organization (if acting for one)"><input value={f.organization} onChange={set("organization")} className={FIELD} data-testid="cr-org"/></L>
        <L label="Your original work (title + link where it can be found) *"><input required value={f.original_work} onChange={set("original_work")} className={FIELD} data-testid="cr-original"/></L>
        <L label="Describe the infringement *"><textarea required minLength={10} rows={4} value={f.description} onChange={set("description")} className={`${FIELD} h-auto py-3`} data-testid="cr-description"/></L>
        <Check checked={f.good_faith} onChange={set("good_faith")} testid="cr-good-faith">I have a good-faith belief that use of the material is not authorized by the copyright owner, its agent, or the law.</Check>
        <Check checked={f.accuracy} onChange={set("accuracy")} testid="cr-accuracy">Under penalty of perjury, the information in this notice is accurate and I am the owner or authorized to act on the owner's behalf.</Check>
        <L label="Electronic signature (type your full name) *"><input required value={f.signature} onChange={set("signature")} className={FIELD} data-testid="cr-signature"/></L>
        <button disabled={busy} data-testid="cr-submit" className="jx-btn h-12 px-8 rounded-full bg-[#00E7FF] text-black font-extrabold">{busy ? "Submitting…" : "Submit claim"}</button>
      </form>
    </div>
  );
}

const L = ({ label, children }) => <label className="block"><span className="text-xs font-semibold text-white/70">{label}</span><div className="mt-1">{children}</div></label>;
const Check = ({ checked, onChange, testid, children }) => (
  <label className="flex gap-3 items-start text-xs text-white/70 cursor-pointer">
    <input type="checkbox" checked={checked} onChange={onChange} data-testid={testid} className="mt-0.5 w-4 h-4 accent-[#00E7FF]"/>
    <span>{children}</span>
  </label>
);
